import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Flex,
  Form,
  Input,
  List,
  Skeleton,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ActionDrawer } from '../../components/ActionDrawer';
import { DetailPage } from '../../components/DetailPage';
import { PrintButton } from '../../components/print/PrintButton';
import { RefundDrawer, RefundList } from '../../components/RefundDrawer';
import { invalidateAfterRefund } from '../../components/refund-api';
import { ApiError } from '../../lib/api-client';
import { invalidateAfterInvoiceMoney } from './invalidate-invoice-money';
import { formatDateTime, orDash } from '../../lib/format';
import { formatMoney, isPositive } from '../../lib/money';
import { getInvoice, voidInvoice, type InvoiceLine } from './api';
import { CreditLinesDrawer } from './CreditLinesDrawer';

const { Text } = Typography;

export function InvoicePage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uid = '' } = useParams<{ uid: string }>();

  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [goodsBack, setGoodsBack] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [crediting, setCrediting] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['invoice', uid], queryFn: () => getInvoice(uid) });

  const cancel = useMutation({
    mutationFn: () => voidInvoice(uid, {
        reason: reason.trim(),
        ...(collected ? { allGoodsReturned: true } : {}),
      }),
    onSuccess: () => {
      invalidateAfterInvoiceMoney(queryClient, { uid, bookingUid: data?.bookingUid ?? null });
      // A void turns the payments into credit, which the customer tab and a refund read.
      invalidateAfterRefund(queryClient);
      setVoiding(false);
      void message.success(t('till.voided'));
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('till.voidFailed'));
    },
  });

  const collected = data?.counterOrder?.status === 'COLLECTED';

  if (isLoading || data === undefined) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  return (
    <>
      <DetailPage
        title={data.customerName ?? t('till.walkIn')}
        subtitle={`${data.invoiceNo}${data.jobNo === null ? '' : ` · ${data.jobNo}`}`}
        tags={[
          {
            label: t(`till.status.${data.status}`),
            colour: data.status === 'PAID' ? 'success' : data.status === 'VOIDED' ? 'error' : undefined,
          },
        ]}
        onBack={() => {
          void navigate('/pos');
        }}
        actions={
          <Flex gap={8} wrap>
            {data.status === 'VOIDED' && data.creditNote != null && (
              <PrintButton
                doc="credit-note"
                uid={data.creditNote.uid}
                label={t('print.printCreditNote')}
                size="middle"
              />
            )}
            {data.status !== 'DRAFT' && (
              <PrintButton doc="invoice" uid={data.uid} label={t('print.printInvoice')} size="middle" />
            )}
            {data.actions?.credit === true && (
              <Button
                onClick={() => {
                  setCrediting(true);
                }}
              >
                {t('pos.creditLines.button')}
              </Button>
            )}
            {data.actions?.void === true && (
              <Button
                danger
                onClick={() => {
                  setReason('');
                  setGoodsBack(false);
                  setVoiding(true);
                }}
              >
                {t('till.void')}
              </Button>
            )}
          </Flex>
        }
        tabs={[
          {
            key: 'lines',
            label: t('till.tabs.lines'),
            content: (
              <Flex vertical gap={16}>
                {data.status === 'VOIDED' && (
                  <Alert
                    type="error"
                    showIcon
                    message={t('till.voidedTitle', { date: formatDateTime(data.voidedAt) })}
                    description={orDash(data.voidReason)}
                  />
                )}

                {data.credit != null && (
                  <Card title={t('till.money.title')}>
                    <Flex vertical gap={12}>
                      <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label={t('till.money.paid')}>{formatMoney(data.paidTotal)}</Descriptions.Item>
                        <Descriptions.Item label={t('till.money.refunded')}>
                          {formatMoney(data.credit.refunded)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('till.money.awaiting')}>
                          {formatMoney(data.credit.awaitingPayout)}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('till.money.refundable')}>
                          <Text strong>{formatMoney(data.credit.refundable)}</Text>
                        </Descriptions.Item>
                        {isPositive(data.credit.notRefundable) && (
                          <Descriptions.Item label={t('till.money.notRefundable')}>
                            {formatMoney(data.credit.notRefundable)}
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                      <RefundList refunds={data.refunds ?? []} />
                      {data.actions?.refund === true && (
                        <Button
                          danger
                          onClick={() => {
                            setRefunding(true);
                          }}
                        >
                          {t('refunds.refund')}
                        </Button>
                      )}
                    </Flex>
                  </Card>
                )}

                {(data.creditNotes ?? []).length > 0 && (
                  <Card title={t('pos.creditNotes.title')}>
                    <List
                      size="small"
                      dataSource={data.creditNotes ?? []}
                      rowKey={(n) => n.uid}
                      renderItem={(n) => (
                        <List.Item>
                          <Flex vertical gap={6} style={{ width: '100%' }}>
                            <Flex justify="space-between" align="center" gap={12} wrap>
                              <Flex vertical gap={2}>
                                <Text strong className="tabular">
                                  {n.creditNoteNo} <Tag>{t(`pos.creditNotes.kind.${n.kind}`)}</Tag>
                                </Text>
                                <Text type="secondary">{formatDateTime(n.issuedAt)}</Text>
                              </Flex>
                              <Flex align="center" gap={12}>
                                <Text strong>{formatMoney(n.amount)}</Text>
                                {n.kind === 'PARTIAL' && (
                                  <PrintButton
                                    doc="credit-note"
                                    uid={n.uid}
                                    label={t('print.printCreditNote')}
                                    size="middle"
                                  />
                                )}
                              </Flex>
                            </Flex>
                            <Text type="secondary">{n.reason}</Text>
                            {n.lines.map((l) => (
                              <Text key={l.invoiceLineUid}>
                                {l.description} × {l.qty} · {formatMoney(l.amount)}
                                {l.backToStock && l.locationName !== null
                                  ? ` · ${t('pos.creditNotes.backToShelf', { location: l.locationName })}`
                                  : ''}
                              </Text>
                            ))}
                          </Flex>
                        </List.Item>
                      )}
                    />
                  </Card>
                )}

                <Card title={t('print.payments')}>
                  {(data.payments ?? []).length === 0 ? (
                    <Text type="secondary">{t('print.noPayments')}</Text>
                  ) : (
                    <List
                      size="small"
                      dataSource={data.payments ?? []}
                      rowKey={(p) => p.uid}
                      renderItem={(p) => (
                        <List.Item>
                          <Flex justify="space-between" align="center" gap={12} wrap style={{ width: '100%' }}>
                            <Flex vertical gap={2}>
                              <Text strong className="tabular">
                                {p.receiptNo}
                              </Text>
                              <Text type="secondary">
                                {formatDateTime(p.receivedAt)} · {t(`till.method.${p.method}`)}
                              </Text>
                            </Flex>
                            <Flex align="center" gap={12}>
                              <Text strong>{formatMoney(p.amount)}</Text>
                              <PrintButton doc="receipt" uid={p.uid} label={t('print.printReceipt')} size="middle" />
                            </Flex>
                          </Flex>
                        </List.Item>
                      )}
                    />
                  )}
                </Card>

                <Card styles={{ body: { padding: 0 } }}>
                  <Table<InvoiceLine>
                    dataSource={data.lines}
                    rowKey={(row) => row.uid}
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    columns={[
                      {
                        title: t('till.fields.item'),
                        dataIndex: 'description',
                        render: (description: string, row: InvoiceLine) => (
                          <Flex vertical gap={2}>
                            <Text>{description}</Text>
                            {/* Every job-card line traces back to a job line —
                                which is what makes "the cashier did not type
                                this" provable rather than asserted. */}
                            {row.fromJobLine && (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {t('till.fromJobCard')}
                              </Text>
                            )}
                          </Flex>
                        ),
                      },
                      {
                        title: t('workshop.fields.qty'),
                        dataIndex: 'qty',
                        width: 90,
                        align: 'right' as const,
                      },
                      {
                        title: t('sales.fields.unitPrice'),
                        dataIndex: 'unitPrice',
                        width: 140,
                        align: 'right' as const,
                        render: (value: string) => formatMoney(value),
                      },
                      {
                        title: t('sales.fields.lineTotal'),
                        dataIndex: 'lineTotal',
                        width: 150,
                        align: 'right' as const,
                        render: (value: string) => <Text strong>{formatMoney(value)}</Text>,
                      },
                    ]}
                    summary={() => (
                      <Table.Summary>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3}>
                            <Text type="secondary">{t('till.fields.total')}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <Text strong>{formatMoney(data.grandTotal)}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        {isPositive(data.creditedTotal ?? '0') && (
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={3}>
                              <Text type="secondary">{t('pos.credited')}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                              <Text strong>{formatMoney(`-${data.creditedTotal ?? '0'}`)}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        )}
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3}>
                            <Text type="secondary">{t('till.fields.due')}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <Text strong type={data.balanceDue === '0' ? 'success' : 'warning'}>
                              {formatMoney(data.balanceDue)}
                            </Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </Card>
              </Flex>
            ),
          },
          {
            key: 'overview',
            label: t('sales.tabs.overview'),
            content: (
              <Card>
                <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                  <Descriptions.Item label={t('till.fields.invoiceNo')}>{data.invoiceNo}</Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.status')}>
                    <Tag>{t(`till.status.${data.status}`)}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.customer')}>
                    {data.customerName ?? t('till.walkIn')}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('workshop.fields.jobNo')}>
                    {orDash(data.jobNo)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('vehicles.fields.plateNo')}>
                    {orDash(data.plateNo)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('till.fields.issued')}>
                    {formatDateTime(data.issuedAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('till.fields.paid')}>
                    {formatMoney(data.paidTotal)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('till.fields.due')}>
                    {formatMoney(data.balanceDue)}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            ),
          },
        ]}
      />

      <ActionDrawer
        open={voiding}
        danger
        title={t('till.void')}
        submitLabel={t('till.void')}
        submitting={cancel.isPending}
        submitDisabled={reason.trim().length < 3 || (collected && !goodsBack)}
        onClose={() => {
          setVoiding(false);
        }}
        onSubmit={() => {
          cancel.mutate();
        }}
        // The invoice is not edited or deleted — a credit note reverses it, and
        // both stay visible because finance keys both into AutoCount.
        consequence={
          !isPositive(data.paidTotal)
            ? t('till.voidConsequence', { invoiceNo: data.invoiceNo })
            : data.source === 'VEHICLE'
              ? t('till.voidConsequenceVehicle', { paid: data.paidTotal })
              : t('till.voidConsequencePaid', { paid: data.paidTotal })
        }
      >
        <Form layout="vertical">
          <Form.Item label={t('workshop.fields.why')} required>
            <Input.TextArea
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder={t('till.voidPlaceholder')}
            />
          </Form.Item>
          {collected && data.counterOrder != null && (
            <Form.Item>
              <Checkbox
                checked={goodsBack}
                onChange={(e) => {
                  setGoodsBack(e.target.checked);
                }}
              >
                {t('till.allGoodsReturned', {
                  location: data.counterOrder.locationName,
                })}
              </Checkbox>
            </Form.Item>
          )}
        </Form>
      </ActionDrawer>

      {data.actions?.credit === true && (
        <CreditLinesDrawer
          invoice={data}
          open={crediting}
          onClose={() => {
            setCrediting(false);
          }}
        />
      )}

      {data.credit != null && (
        <RefundDrawer
          source={{ invoiceUid: data.uid }}
          refundable={data.credit.refundable}
          open={refunding}
          onClose={() => {
            setRefunding(false);
          }}
          onDone={() => undefined}
        />
      )}
    </>
  );
}
